import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {AddOutlined} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointLoyaltyPurchasesDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import Button from '../../../../../../components/base/button/button';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import StatusPill from '../../components/status-pill';
import {paginateMockItems} from '../../mock-search';
import {mockBusiness, mockPurchases} from '../../mock-data';
import PurchaseBizDialog from './purchase-biz-dialog';

const dataGridSaveConfig = {name: 'BizPointLoyaltyPurchasesDataGrid', version: 2};
const col = BizPointLoyaltyPurchasesDataGridColumnNames;

const searchOptions = {
    keywordFields: ['ref', 'status'],
    sortFieldMap: {
        [col.ref]: 'ref',
        [col.date]: 'date',
        [col.amount]: 'amt',
        [col.biz]: 'biz',
    },
};

const LoyaltyBuyView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.buy'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportLoyaltyBuyBiz = operatorAccessLevels.packages.bizPoint.exportLoyaltyBuyBiz;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-loyalty-buy');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [sourceItems, setSourceItems] = useState(mockPurchases);
    const [data, setData] = useState([]);
    const [purchaseOpen, setPurchaseOpen] = useState(false);

    /**
     * Configures notebook tags and behavioral analytics for the Biz Purchases page.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointLoyaltyBuy);
        configureBehavioralTool((tags) => tags.bizPointLoyaltyBuy);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {title: t('ref'), name: col.ref, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('date'), name: col.date, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('amount'), name: col.amount, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.right},
        {title: t('biz'), name: col.biz, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('status'), name: col.status, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.left, sortable: false},
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads purchase history from mock data.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(sourceItems, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted, sourceItems]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(sourceItems, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.amount]: (item) => `${item.amt.toLocaleString()} ${mockBusiness.currency}`,
            [col.status]: (item) => item.status,
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, sourceItems, t]);

    /**
     * Appends a newly created purchase to the history grid.
     */
    const handlePurchaseCreated = useCallback((newPurchase) => {
        setSourceItems((prev) => [newPurchase, ...prev]);
    }, []);

    const rows = useMemo(() => data.map((row) => ({
        key: row.ref,
        data: row,
        cells: {
            [col.ref]: row.ref,
            [col.date]: row.date,
            [col.amount]: `${row.amt.toLocaleString()} ${mockBusiness.currency}`,
            [col.biz]: row.biz.toLocaleString(),
            [col.status]: <StatusPill status={row.status}/>,
        },
    })), [data]);

    return (
        <Box>
            <PageHeader
                title={t('title')}
                actions={
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddOutlined/>}
                        onClick={() => setPurchaseOpen(true)}
                    >
                        {t('purchaseAction')}
                    </Button>
                }
            />

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportLoyaltyBuyBiz ? getExportInfo : undefined}
            />

            <PurchaseBizDialog
                open={purchaseOpen}
                onClose={() => setPurchaseOpen(false)}
                onCreated={handlePurchaseCreated}
            />
        </Box>
    );
};

export default LoyaltyBuyView;
